import {
  Component,
  Input,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
} from "@angular/core";
import { environment } from "../../../environments/environment";
import { Epic } from "../../interfaces/epic";
import { MzBaseModal, MzModalService } from "ng2-materialize";
import { Project } from "../../interfaces/project";
import { Utils } from "../../utils/utils";
import { Subject } from "rxjs";
import { EpicService } from "../../services/epic/epic.service";
import { ConfirmEpicSwitchDialogComponent } from "../confirm-epic-switch-dialog/confirm-epic-switch-dialog.component";
import { formArrayNameProvider } from "@angular/forms/src/directives/reactive_directives/form_group_name";
import { EpicGalleryDialogComponent } from "../epic-gallery/epic-gallery.component";
import { Annotation } from "../../interfaces/annotation";
import { Router, NavigationExtras } from "@angular/router";

@Component({
  selector: "app-edit-epic-detail-dialog",
  templateUrl: "./edit-epic-detail-dialog.component.html",
  styleUrls: ["./edit-epic-detail-dialog.component.scss"],
})
export class EditEpicDetailDialogComponent
  extends MzBaseModal
  implements OnInit {
  readonly env: any = environment;
  readonly presetThemeColors = Utils.presetThemeColors;
  private resultSubject: Subject<[Epic, Epic]>;

  @Input() project: Project;

  @ViewChild("editEpicForm") editEpicForm;

  @ViewChild("Gallery") gallery: EpicGalleryDialogComponent;

  newTheme: Epic;
  allEpics: Epic[];
  availableEpics: Epic[];
  allAnnotations: Annotation[];
  availableAnnotations: Annotation[];
  selectedEpic: any = null;
  selectedAnnotation: any = null;

  // title of the modal dialog window that is used in the HTML file
  public title: string = "Epic bearbeiten";

  public dependencyFromChips: Materialize.ChipDataObject[] = [
    // { tag: 'Epic 1' },
  ];

  public dependencyToChips: Materialize.ChipDataObject[] = [
    // { tag: 'Epic 1' },
  ];
  public annotationChips: Materialize.ChipDataObject[] = [
    // { tag: 'Annotation 1' },
  ];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: MzModalService,
    private router: Router
  ) {
    super();
  }

  ngAfterViewInit() {}

  ngOnInit() {
    this.resultSubject = new Subject<[Epic, Epic]>();
  }

  async save(navigateToEpic: Epic = null) {
    if (!this.resultSubject.closed) {
      this.resultSubject.next([this.newTheme, navigateToEpic]);
      this.resultSubject.complete();
      this.modalComponent.close();
    }
    this.newTheme = { color: this.presetThemeColors[0][0] } as Epic;
  }

  async abort() {
    this.modalComponent.close();
    await this.modalComponent.onClose
      .map((_) => true)
      .first()
      .toPromise();
    this.resultSubject.next(null);
    this.newTheme = { color: this.presetThemeColors[0][0] } as Epic;
  }

  async showAttachments() {
    this.modalComponent.close();
    await this.modalComponent.onClose
      .map((_) => true)
      .first()
      .toPromise();
    this.newTheme.numberOfAttachments = await this.gallery.show(
      this.newTheme._id,
      this.newTheme.name,
      "Epic"
    );
    this.changeDetectorRef.detectChanges();
    this.modalComponent.open();
  }

  async navigateToEpic(navigateToEpic: Epic) {
    if (!this.resultSubject.closed) {
      this.resultSubject.next([null, navigateToEpic]);
      this.resultSubject.complete();
    }
    this.newTheme = { color: this.presetThemeColors[0][0] } as Epic;
    this.modalComponent.close();
  }

  /**
   * Opens the modal dialog for creating a new epic or modyfing an existing one.
   * This method will not return until the dialog has been closed (either via Save or Cancel).
   * @param epic The existing epic that should be edited in this dialog, or null if a new epic should be created.
   * @param epics A list of all existing epics in the current project.
   * @param annotations A list of all existing annotations in the current project.
   */
  async getEditResultAsync(
    epic: Epic,
    epics: Epic[],
    annotations: Annotation[]
  ) {
    // set window title
    if (epic._id === undefined) this.title = "Epic erstellen";
    else this.title = "Epic bearbeiten";

    this.newTheme = JSON.parse(JSON.stringify(epic));

    this.changeDetectorRef.detectChanges();
    this.modalComponent.open();
    setTimeout(Materialize.updateTextFields, 10);
    this.resultSubject = new Subject<[Epic, Epic]>();

    if (this.newTheme) {
      this.allEpics = epics;
      this.availableEpics = this.allEpics.filter(
        (e) => e.name !== this.newTheme.name
      );
      this.allAnnotations = annotations;
      this.availableAnnotations = this.allAnnotations;

      // Empty chips and selectedValues in case the information was not saved
      this.dependencyFromChips = [];
      this.dependencyToChips = [];
      this.annotationChips = [];
      this.selectedEpic = null;
      this.selectedAnnotation = null;

      this.newTheme.epicLinksFrom.forEach((l) => {
        const epicToAdd: Epic = this.allEpics.find((e) => e._id === l);
        this.dependencyFromChips.push({ tag: epicToAdd.name });
      });

      this.newTheme.epicLinksTo.forEach((l) => {
        const epicToAdd: Epic = this.allEpics.find((e) => e._id === l);
        this.dependencyToChips.push({ tag: epicToAdd.name });
      });

      // Update annotationChips for already saved epic links
      this.newTheme.annotationLinks.forEach((l) => {
        const annotationToAdd: Annotation = this.allAnnotations.find(
          (e) => e._id === l
        );
        this.annotationChips.push({
          tag: "Annotation #" + annotationToAdd.number,
        });
      });

      // Remove already added epics from available epics
      this.dependencyFromChips.forEach((c) => {
        this.availableEpics = this.availableEpics
          .filter((e) => e.name !== c.tag)
          .sort((a, b) => (a.name < b.name ? -1 : 1));
      });

      // Remove already added annotations from available annotations
      this.annotationChips.forEach((c) => {
        this.availableAnnotations = this.availableAnnotations.filter(
          (e) => e.number !== parseInt(c.tag.substring(12), 10)
        );
      });
    }

    return this.resultSubject.first().toPromise();
  }

  addAnnotationAsDependency() {
    const selectedAnnotationNr = parseInt(
      this.selectedAnnotation.substring(12),
      10
    ); // Extracts the AnnotationNr from the String
    if (
      this.annotationChips.filter((c) => c.tag === this.selectedAnnotation)
        .length === 0 &&
      this.selectedAnnotation
    ) {
      // Update UI
      this.annotationChips.push({ tag: this.selectedAnnotation });
      this.availableAnnotations = this.availableAnnotations.filter(
        (e) => e.number !== selectedAnnotationNr
      ); // Not sure if i need this: .sort((a, b) => a.name < b.name ? -1 : 1);

      // Add the link to the epic model
      const addedAnnotation: Annotation = this.allAnnotations.find(
        (e) => e.number === selectedAnnotationNr
      );
      if (this.newTheme.annotationLinks.indexOf(addedAnnotation._id) === -1) {
        // Just to making really really sure AnnotationIds don't get added twice.
        this.newTheme.annotationLinks.push(addedAnnotation._id);
      }
      // Reset the selected value
      this.selectedAnnotation = null;
    }
  }

  addEpicAsDependency() {
    if (
      this.dependencyFromChips.filter((c) => c.tag === this.selectedEpic)
        .length === 0 &&
      this.selectedEpic
    ) {
      const addedEpic: Epic = this.allEpics.find(
        (e) => e._id === this.selectedEpic
      );

      // Update UI
      this.dependencyFromChips.push({ tag: addedEpic.name });
      this.availableEpics = this.availableEpics
        .filter((e) => e._id !== this.selectedEpic)
        .sort((a, b) => (a.name < b.name ? -1 : 1));

      // Add the link to the epic model
      this.newTheme.epicLinksFrom.push(addedEpic._id);

      // Reset the selected value

      this.selectedEpic = null;
    }
  }

  removeEpicAsDependency(event) {
    // Update UI
    this.dependencyFromChips = this.dependencyFromChips.filter(
      (c) => c.tag !== event
    );
    this.availableEpics.push(this.allEpics.find((e) => e.name === event));
    this.availableEpics = this.availableEpics.sort((a, b) =>
      a.name < b.name ? -1 : 1
    );
    this.editEpicForm.controls["epicLinksFrom"].markAsDirty();

    // Remove the link from the epic model
    const removedEpic: Epic = this.allEpics.find((e) => e.name === event);
    this.newTheme.epicLinksFrom = this.newTheme.epicLinksFrom.filter(
      (l) => l !== removedEpic._id
    );
  }

  removeAnnotationAsDependency(event) {
    // Update UI
    var no = parseInt(event.substring(12), 10);
    this.annotationChips = this.annotationChips.filter((c) => c.tag !== event);
    this.availableAnnotations.push(
      this.allAnnotations.find((e) => e.number == no)
    );
    this.availableAnnotations = this.availableAnnotations.sort((a, b) =>
      a.number < b.number ? -1 : 1
    );
    this.editEpicForm.controls["annotationLinks"].markAsDirty();

    // Remove the link from the epic model
    const removedAnnotation: Annotation = this.allAnnotations.find(
      (e) => e.number === no
    );
    this.newTheme.annotationLinks = this.newTheme.annotationLinks.filter(
      (l) => l !== removedAnnotation._id
    );
  }

  async dependencySelected(epicName) {
    const clickedEpic: Epic = this.allEpics.find((e) => e.name === epicName);

    // If the form is dirty
    if (this.editEpicForm.dirty) {
      // Open a modal and ask the user if he wants to save the changes or abort the action
      this.modalComponent.close();
      const result = await (<ConfirmEpicSwitchDialogComponent>(
        this.modalService.open(ConfirmEpicSwitchDialogComponent).instance
      )).result
        .asObservable()
        .first()
        .toPromise();
      if (result === true) {
        // Save the epic and navigate
        await this.save(clickedEpic);
        this.editEpicForm.reset();
      } else if (result === false) {
        // Navigate to the clicked epic without saving
        this.navigateToEpic(clickedEpic);
        this.editEpicForm.reset();
      } else {
        this.modalComponent.open();
      }
    } else {
      this.navigateToEpic(clickedEpic);
      this.editEpicForm.reset();
    }
  }

  async annotationSelected(annotationName) {
    // If the form is dirty
    if (this.editEpicForm.dirty) {
      // Open a modal and ask the user if he wants to save the changes or abort the action
      this.modalComponent.close();
      const result = await (<ConfirmEpicSwitchDialogComponent>(
        this.modalService.open(ConfirmEpicSwitchDialogComponent).instance
      )).result
        .asObservable()
        .first()
        .toPromise();
      if (result === true) {
        // Save the epic and navigate
        await this.save();
        this.router.navigate(
          ["/projects/" + this.project.shortId + "/annotations"],
          { fragment: annotationName.substring(12), preserveFragment: false }
        );
        this.editEpicForm.reset();
      } else if (result === false) {
        // Navigate to the clicked epic without saving
        //this.navigateToEpic(clickedEpic);
        this.router.navigate(
          ["/projects/" + this.project.shortId + "/annotations"],
          { fragment: annotationName.substring(12), preserveFragment: false }
        );
        this.editEpicForm.reset();
      } else {
        this.modalComponent.open();
      }
    } else {
      //this.navigateToEpic(clickedEpic);
      this.modalComponent.close();
      this.router.navigate(
        ["/projects/" + this.project.shortId + "/annotations"],
        { fragment: annotationName.substring(12), preserveFragment: false }
      );
      this.editEpicForm.reset();
    }
  }
}



// WEBPACK FOOTER //
// /agir/src/app/components/edit-epic-detail-dialog/edit-epic-detail-dialog.component.ts